/**
 * Client API des fichiers de périmètre (entité PerimetreFile côté backend).
 * Remplace l'accès direct Azure Blob Storage du frontend.
 */

import { BlockBlobClient } from '@azure/storage-blob';
import type { IndexationInfo } from '../utils/indexationLabels';
import type { Droits, Oeuvre } from '../utils/oeuvre';

export type { Droits, Oeuvre } from '../utils/oeuvre';

export interface PerimetreFileDto {
    id: string;
    name: string;          // blobPath
    displayName: string;
    mimeType: string;
    size?: number;
    perimetreIds: string[];
    url: string;
    /** État de préparation pour l'assistant (C19) ; absent = document jamais demandé. */
    indexation?: IndexationInfo;
    /** L'œuvre derrière le document (C27 SP6) : « Auteur, Titre » signe les citations qui sortent du compte. */
    oeuvre?: Oeuvre;
    /** Déclaration de droits d'une organisation sur un document communiqué (C27 SP6). */
    droits?: Droits;
}

/** Shape compatible avec l'ancien BlobItem consommé par les composants */
export interface BlobFile {
    id: string;
    name: string;
    url: string;
    /** Le type réel du fichier, tel que le serveur le connaît : un nom sans extension s'y reconnaît (fumée O6 du 03/11/2026). */
    mimeType?: string;
    /** L'œuvre derrière le document (C27 SP6), si renseignée. */
    oeuvre?: Oeuvre;
    /**
     * Taille en octets, telle que le serveur la connaît. Deux enregistrements
     * qui portent le même nom ET la même taille sont deux copies du même
     * document : c'est ce qui permet à la liste « Mes documents » du mobile de
     * n'en montrer qu'une (`reader/documentKinds.ts`).
     */
    size?: number;
    metadata: { name: string; perimetreId?: string };
    perimetreId?: string;
    /** État de préparation pour l'assistant (C19) ; absent = document jamais demandé. */
    indexation?: IndexationInfo;
}

export function toBlobFile(dto: PerimetreFileDto): BlobFile {
    const perimetreId = dto.perimetreIds[0];
    return {
        id: dto.id,
        name: dto.name,
        url: dto.url,
        ...(dto.mimeType ? { mimeType: dto.mimeType } : {}),
        ...(dto.size !== undefined ? { size: dto.size } : {}),
        ...(dto.indexation ? { indexation: dto.indexation } : {}),
        ...(dto.oeuvre ? { oeuvre: dto.oeuvre } : {}),
        metadata: { name: dto.displayName, ...(perimetreId ? { perimetreId } : {}) },
        ...(perimetreId ? { perimetreId } : {}),
    };
}

export async function listPerimetreFiles(token: string, perimetreId?: string): Promise<PerimetreFileDto[]> {
    const url = perimetreId ? `/api/perimetre-files/${perimetreId}` : '/api/perimetre-files';
    // Azure SWA peut altérer le header Authorization : on envoie aussi 'accesstoken', non modifié.
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}`, accesstoken: token } });
    if (!res.ok) throw new Error(`perimetre-files ${res.status}`);
    const data = await res.json();
    // `degraded` : l'API n'a pas pu joindre le storage. Une liste vide ne
    // signifie alors pas « aucun fichier » mais « impossible de savoir ».
    if (data.degraded) {
        console.error(
            '[perimetre-files] Listing dégradé : le storage est injoignable côté API. '
            + 'Vérifier les app settings AZURE_STORAGE_RESOURCE_NAME / AZURE_STORAGE_SAS_TOKEN.'
        );
    }
    return data.files ?? [];
}

export async function syncPerimetreFiles(token: string): Promise<{ scanned: number; upserted: number; removed: number }> {
    // Azure SWA peut altérer le header Authorization : on envoie aussi 'accesstoken', non modifié.
    const res = await fetch('/api/perimetre-files-sync', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, accesstoken: token },
    });
    if (!res.ok) throw new Error(`perimetre-files-sync ${res.status}`);
    return res.json();
}

export async function uploadPerimetreFile(
    token: string, perimetreId: string, file: File
): Promise<{ fileId: string; blobPath: string }> {
    // Azure SWA peut altérer le header Authorization : on envoie aussi 'accesstoken', non modifié.
    const createRes = await fetch('/api/perimetre-files', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, accesstoken: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ perimetreId, fileName: file.name, mimeType: file.type, size: file.size }),
    });
    if (!createRes.ok) throw new Error(`perimetre-files create ${createRes.status}`);
    const { fileId, blobPath, uploadUrl } = await createRes.json();

    // Upload direct au storage avec le SAS court à destination unique.
    // Métadonnées posées pour compat avec la réconciliation (sync) et le legacy.
    const client = new BlockBlobClient(uploadUrl);
    await client.uploadData(file, {
        blobHTTPHeaders: { blobContentType: file.type || 'application/octet-stream' },
        metadata: { name: encodeURI(file.name), perimetreid: perimetreId },
    });

    // Azure SWA peut altérer le header Authorization : on envoie aussi 'accesstoken', non modifié.
    const completeRes = await fetch(`/api/perimetre-files/${fileId}/complete`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, accesstoken: token },
    });
    if (!completeRes.ok) throw new Error(`perimetre-files complete ${completeRes.status}`);

    return { fileId, blobPath };
}

/**
 * Renomme un fichier. Seul le nom affiché change : les octets, le nom de blob
 * et les URLs déjà distribuées restent intacts.
 *
 * ⚠️ Le serveur réimpose l'extension d'origine — le nom retenu peut différer de
 * celui envoyé. C'est le `displayName` de la réponse qui fait foi.
 */
export async function renamePerimetreFile(
    token: string, fileId: string, displayName: string
): Promise<PerimetreFileDto> {
    // Azure SWA peut altérer le header Authorization : on envoie aussi 'accesstoken', non modifié.
    const res = await fetch(`/api/perimetre-files/${fileId}/rename`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, accesstoken: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName }),
    });
    if (!res.ok) throw new Error(`perimetre-files rename ${res.status}`);
    const { file } = await res.json();
    return file;
}

/**
 * L'œuvre derrière un document (C27 SP6) : `{ titre?, auteur? }`, deux champs
 * vides effacent. Propriétaire seulement (404 sur un dossier partagé par autrui).
 */
export async function setPerimetreFileOeuvre(token: string, fileId: string, oeuvre: Oeuvre): Promise<PerimetreFileDto> {
    const res = await fetch(`/api/perimetre-files/${fileId}/oeuvre`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, accesstoken: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ titre: oeuvre.titre ?? '', auteur: oeuvre.auteur ?? '' }),
    });
    if (!res.ok) throw new Error(`perimetre-files oeuvre ${res.status}`);
    const { file } = await res.json();
    return file;
}

export async function deletePerimetreFile(token: string, fileId: string): Promise<void> {
    // Azure SWA peut altérer le header Authorization : on envoie aussi 'accesstoken', non modifié.
    const res = await fetch(`/api/perimetre-files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, accesstoken: token },
    });
    // 404 = suppression idempotente : le fichier est déjà absent (balayage de périmètre
    // concurrent, double-clic...), ce n'est pas une erreur pour l'appelant.
    if (!res.ok && res.status !== 404) throw new Error(`perimetre-files delete ${res.status}`);
}
